import { FC, useCallback, useEffect } from 'react';
import { useHistory, useLocation, matchPath } from 'react-router';

import { selectEditableStoryId } from 'admin/reducers/story-editor/selectors';
import { selectUserInfo } from 'admin/reducers/user/selectors';
import { STORIES_PAGE, STORY_PAGE } from 'admin/constants/routes';
import { setModal } from 'admin/actions/set-modal';
import { useAdminSelector, useAdminDispatch } from 'admin/reducers';
import { MODAL } from 'admin/constants/common';
import { last } from 'lodash';
import Pubnub from 'pubnub';
import { usePubnubFacade } from 'admin/hooks/pubnub/use-pubnub-facade';
import type PubnubFacade from 'common/utils/facades/pubnub-facade';
import {
	hasOtherOccupant,
	isJoinMessageEvent,
	isOccupiedBy,
	StoryCoEditingMessageType,
	type StoryJoinMessage,
} from './story-co-editing-pubnub-utils';

const CoEditingObserver: FC = () => {
	const dispatch = useAdminDispatch();
	const pubnub = usePubnubFacade();
	const { push: historyPush } = useHistory();
	const { pathname } = useLocation();
	const storyId = useAdminSelector(selectEditableStoryId);
	const { email, name = 'noname', id: userId } = useAdminSelector(selectUserInfo);
	const navigateToStories = useCallback(() => historyPush(STORIES_PAGE), [historyPush]);
	const isStoryRoute = Boolean(matchPath(pathname, { path: STORY_PAGE }));

	// join/leave
	useEffect(() => {
		// Any entry into story route (including browser back) must re-run the same conflict check path.
		const channelName = isStoryRoute ? storyId : undefined;

		if (channelName !== undefined) {
			// join/leave handlers
			const subscribeToStoryChannel = async (pubnubFacade: PubnubFacade) => {
				await pubnubFacade.publish({
					channel: channelName,
					message: { email, name, type: StoryCoEditingMessageType.join } as StoryJoinMessage['message'],
				});
				pubnubFacade.subscribe(channelName);
				pubnubFacade.addListeners({
					message(messageEvent: Pubnub.MessageEvent) {
						// force kick out
						if (
							isJoinMessageEvent(messageEvent) &&
							messageEvent.message.email &&
							messageEvent.message.email !== email
						) {
							navigateToStories();

							// Display the modal only after a location change to prevent automatic
							// closure by `ModalManager`
							setTimeout(() => {
								dispatch(
									setModal({
										id: MODAL.KICK_OUT_INFO_MODAL,
										data: {
											name: messageEvent.message.name,
											email: messageEvent.message.email,
										},
									})
								);
							}, 100);
						}
					},
				});
			};
			// attempt to join
			const joinToStoryChannel = async (pubnubFacade: PubnubFacade) => {
				const occupants = await pubnubFacade.getChannelOccupants(channelName);

				if (hasOtherOccupant(occupants, userId)) {
					// Presence can be briefly stale during route/unmount race; one fast recheck reduces false conflicts
					const recheckedOccupants = await pubnubFacade.getChannelOccupants(channelName);

					if (!hasOtherOccupant(recheckedOccupants, userId)) {
						await subscribeToStoryChannel(pubnubFacade);
						return;
					}

					const response = await pubnubFacade.fetchMessaged({ channels: [channelName], count: 1 });
					const channelMsgEvents = response.channels[channelName];
					const lastMsgEvent = last(channelMsgEvents);
					const isLastMsgFromAnotherEditor = isOccupiedBy(lastMsgEvent, pubnubFacade.uuid);

					// `hereNow` decides occupancy; last message is used only to prefill modal identity best-effort.
					const modalUserName = isLastMsgFromAnotherEditor ? lastMsgEvent.message.name : 'noname';
					const modalUserEmail = isLastMsgFromAnotherEditor ? lastMsgEvent.message.email : 'noname';

					dispatch(
						setModal({
							id: MODAL.STORY_EDITORS_CONFLICT,
							data: {
								name: modalUserName ?? 'noname',
								email: modalUserEmail ?? 'noname',
								onOk: () => subscribeToStoryChannel(pubnubFacade),
								onCancel: navigateToStories,
							},
						})
					);

					return;
				}

				await subscribeToStoryChannel(pubnubFacade);
			};

			if (pubnub) {
				joinToStoryChannel(pubnub);
			}
		}

		return () => {
			if (pubnub && channelName) {
				pubnub.removeListeners();
				pubnub.unsubscribe(channelName);
			}
		};
	}, [pubnub, userId, email, name, storyId, dispatch, navigateToStories, isStoryRoute]);

	return null;
};

export default CoEditingObserver;
